import { database } from '../config/backend';
import { ref, get as rtdbGet, set as rtdbSet, push, query, orderByChild, equalTo, limitToFirst, remove as rtdbRemove } from './phpDataAdapter';

export type InstructionCategory = 
  | 'general' 
  | 'weight_loss' 
  | 'weight_gain' 
  | 'diabetes' 
  | 'hypertension' 
  | 'cholesterol' 
  | 'pregnancy' 
  | 'sports' 
  | 'vegetarian' 
  | 'vegan'
  | 'digestive'
  | 'allergies'
  | 'nutrition'
  | 'meal_planning'
  | 'recipes'
  | 'meal_timing'
  | 'hydration'
  | 'supplements'
  | 'exercise'
  | 'cardio'
  | 'strength_training'
  | 'flexibility'
  | 'recovery'
  | 'pre_workout_nutrition'
  | 'post_workout_nutrition';

export interface NutritionInstruction {
  id: string;
  title: string;
  category: InstructionCategory;
  content: string; // Markdown ou HTML
  tags: string[];
  createdBy: string; // nutritionistId
  isPublic: boolean; // se pode ser usado por outros nutricionistas
  attachments?: string[]; // URLs de imagens ou PDFs
  createdAt: string;
  updatedAt?: string;
}

// Instruções pré-cadastradas do sistema
const PRELOADED_INSTRUCTIONS: Omit<NutritionInstruction, 'id' | 'createdBy' | 'createdAt' | 'isPublic'>[] = [
  {
    title: 'Alimentação Saudável - Guia Geral',
    category: 'general',
    content: `# Alimentação Saudável - Guia Geral

## Princípios Básicos

Uma alimentação saudável é fundamental para manter a saúde e prevenir doenças. Siga estes princípios:

### 1. Variedade
- Consuma alimentos de todos os grupos alimentares
- Varie as cores, texturas e sabores
- Evite monotonia alimentar

### 2. Moderação
- Controle as porções
- Evite excessos
- Mantenha o equilíbrio

### 3. Regularidade
- Faça 5-6 refeições por dia
- Não pule refeições
- Mantenha horários regulares

### 4. Hidratação
- Beba pelo menos 2 litros de água por dia
- Prefira água pura
- Evite bebidas açucaradas

## Grupos Alimentares

### Proteínas
- Carnes magras, peixes, ovos, leguminosas
- Importantes para construção e reparo dos tecidos

### Carboidratos
- Arroz, batata, pão, massas (preferir integrais)
- Fonte principal de energia

### Vegetais e Frutas
- Rico em vitaminas, minerais e fibras
- Consuma pelo menos 5 porções ao dia

### Laticínios
- Fonte de cálcio e proteínas
- Prefira versões desnatadas ou semidesnatadas`,
    tags: ['alimentação saudável', 'geral', 'guia'],
  },
  {
    title: 'Perda de Peso - Orientações Nutricionais',
    category: 'weight_loss',
    content: `# Perda de Peso - Orientações Nutricionais

## Objetivo
Criar um déficit calórico moderado através de alimentação equilibrada e hábitos saudáveis.

## Estratégias

### 1. Controle de Porções
- Use pratos menores
- Meça porções com colheres e xícaras
- Evite repetições

### 2. Escolhas Inteligentes
- Prefira alimentos integrais
- Aumente consumo de vegetais
- Reduza alimentos processados
- Evite açúcares e gorduras saturadas

### 3. Distribuição de Refeições
- Café da manhã: 20-25% das calorias
- Lanche da manhã: 5-10%
- Almoço: 30-35%
- Lanche da tarde: 5-10%
- Jantar: 20-25%
- Lanche da noite (se necessário): 5%

### 4. Hidratação
- Beba água antes das refeições
- Evite bebidas calóricas
- Água ajuda na saciedade

## Alimentos Recomendados
- Vegetais folhosos (alface, espinafre, rúcula)
- Proteínas magras (peito de frango, peixe, ovos)
- Carboidratos integrais (arroz integral, batata doce)
- Frutas com baixo índice glicêmico (maçã, pera, morango)`,
    tags: ['perda de peso', 'emagrecimento', 'dieta'],
  },
  {
    title: 'Diabetes - Orientações Alimentares',
    category: 'diabetes',
    content: `# Diabetes - Orientações Alimentares

## Objetivo
Manter níveis glicêmicos estáveis através de alimentação controlada em carboidratos.

## Princípios

### 1. Controle de Carboidratos
- Distribua carboidratos ao longo do dia
- Prefira carboidratos de baixo índice glicêmico
- Conte carboidratos para ajuste de insulina (se aplicável)

### 2. Índice Glicêmico
**Baixo IG (preferir):**
- Aveia, quinoa, batata doce
- Frutas: maçã, pera, morango
- Vegetais não-amiláceos

**Alto IG (evitar):**
- Açúcar, mel, doces
- Pão branco, arroz branco
- Batata inglesa, melancia

### 3. Fibras
- Aumente consumo de fibras
- Ajuda no controle glicêmico
- Melhora saciedade

### 4. Proteínas e Gorduras
- Inclua proteínas em todas as refeições
- Prefira gorduras insaturadas
- Evite gorduras trans

## Exemplo de Cardápio
- Café da manhã: Aveia com frutas e iogurte
- Lanche: Castanhas
- Almoço: Salada + proteína + carboidrato integral
- Lanche: Fruta com queijo
- Jantar: Vegetais + peixe + batata doce`,
    tags: ['diabetes', 'glicemia', 'controle'],
  },
  {
    title: 'Hipertensão - Orientações Nutricionais',
    category: 'hypertension',
    content: `# Hipertensão - Orientações Nutricionais

## Objetivo
Reduzir pressão arterial através de alimentação com baixo teor de sódio e rica em potássio.

## Princípios

### 1. Redução de Sódio
- Evite adicionar sal aos alimentos
- Limite consumo de alimentos processados
- Leia rótulos nutricionais
- Meta: menos de 2g de sódio por dia

### 2. Aumento de Potássio
- Consuma frutas e vegetais ricos em potássio
- Banana, abacate, batata, espinafre
- Ajuda a equilibrar sódio

### 3. DASH (Dietary Approaches to Stop Hypertension)
- Rica em frutas, vegetais e grãos integrais
- Inclui laticínios com baixo teor de gordura
- Limita gorduras saturadas e colesterol

### 4. Alimentos a Evitar
- Embutidos (presunto, salsicha, salame)
- Enlatados com sal
- Queijos amarelos
- Fast food
- Molhos prontos

## Alimentos Recomendados
- Vegetais frescos
- Frutas
- Grãos integrais
- Proteínas magras
- Laticínios desnatados`,
    tags: ['hipertensão', 'pressão arterial', 'sódio'],
  },
  {
    title: 'Ganho de Peso - Orientações Nutricionais',
    category: 'weight_gain',
    content: `# Ganho de Peso - Orientações Nutricionais

## Objetivo
Aumentar peso de forma saudável através de superávit calórico controlado.

## Estratégias

### 1. Aumento Calórico Gradual
- Adicione 300-500 calorias ao dia
- Aumente porções gradualmente
- Não pule refeições

### 2. Alimentos Calóricos e Nutritivos
- Abacate, castanhas, azeite
- Proteínas: carnes, peixes, ovos, leguminosas
- Carboidratos: arroz, batata, massas
- Laticínios integrais

### 3. Frequência de Refeições
- 5-6 refeições por dia
- Lanches entre refeições principais
- Não fique mais de 3 horas sem comer

### 4. Exercícios
- Combine com treino de força
- Exercícios ajudam a ganhar massa muscular
- Não apenas gordura

## Exemplo de Lanches Calóricos
- Iogurte com granola e frutas
- Pão integral com abacate e ovo
- Smoothie com frutas e proteína
- Castanhas e frutas secas`,
    tags: ['ganho de peso', 'aumento de peso', 'massa'],
  },
];

/**
 * Inicializa o banco de dados com instruções pré-cadastradas
 */
export async function initializeNutritionInstructions(): Promise<void> {
  try {
    const instructionsRef = ref(database, 'nutritionInstructionsLibrary');
    const snapshot = await rtdbGet(instructionsRef);
    
    if (!snapshot.exists()) {
      // Banco vazio, adicionar instruções pré-cadastradas
      for (const instruction of PRELOADED_INSTRUCTIONS) {
        const categoryRef = ref(database, `nutritionInstructionsLibrary/${instruction.category}`);
        const instructionRef = push(categoryRef);
        await rtdbSet(instructionRef, {
          ...instruction,
          id: instructionRef.key,
          createdAt: new Date().toISOString(),
        });
      }
    }
  } catch (error: any) {
    // Erro de permissão é esperado se as autorização PHP/MySQL não estiverem configuradas
    // Não interrompe a execução do app, apenas loga o erro
    const errorMessage = error?.message || String(error);
    const isPermissionError = 
      error?.code === 'PERMISSION_DENIED' || 
      errorMessage.includes('Permission denied') ||
      errorMessage.includes('permission_denied');
    
    if (isPermissionError) {
      console.warn('⚠️ Permissão negada no servidor PHP/MySQL. Configure as regras do Realtime Database para habilitar as instruções nutricionais.');
    } else {
      console.error('Erro ao inicializar instruções nutricionais:', error);
    }
  }
}

/**
 * Busca instruções nutricionais
 */
export async function searchNutritionInstructions(
  searchTerm?: string,
  category?: InstructionCategory,
  createdBy?: string,
  includePublic: boolean = true
): Promise<NutritionInstruction[]> {
  try {
    const instructions: NutritionInstruction[] = [];
    
    // Buscar instruções do usuário
    if (createdBy) {
      const userInstructionsRef = ref(database, `nutritionInstructions/${createdBy}`);
      const userSnapshot = await rtdbGet(userInstructionsRef);
      
      if (userSnapshot.exists()) {
        const data = userSnapshot.val();
        Object.entries(data).forEach(([id, instruction]: [string, any]) => {
          instructions.push({
            id,
            ...instruction,
          });
        });
      }
    }
    
    // Buscar instruções públicas
    if (includePublic) {
      const publicInstructionsRef = ref(database, 'nutritionInstructions');
      const publicSnapshot = await rtdbGet(publicInstructionsRef);
      
      if (publicSnapshot.exists()) {
        const data = publicSnapshot.val();
        Object.entries(data).forEach(([userId, userInstructions]: [string, any]) => {
          if (typeof userInstructions === 'object') {
            Object.entries(userInstructions).forEach(([id, instruction]: [string, any]) => {
              if (instruction.isPublic && (!createdBy || userId !== createdBy)) {
                instructions.push({
                  id,
                  ...instruction,
                });
              }
            });
          }
        });
      }
      
      // Buscar instruções da biblioteca do sistema
      const libraryRef = ref(database, 'nutritionInstructionsLibrary');
      const librarySnapshot = await rtdbGet(libraryRef);
      
      if (librarySnapshot.exists()) {
        const data = librarySnapshot.val();
        Object.entries(data).forEach(([cat, categoryInstructions]: [string, any]) => {
          if (typeof categoryInstructions === 'object') {
            Object.entries(categoryInstructions).forEach(([id, instruction]: [string, any]) => {
              instructions.push({
                id,
                ...instruction,
                createdBy: 'system',
                isPublic: true,
              });
            });
          }
        });
      }
    }
    
    // Filtrar por categoria
    let filtered = category 
      ? instructions.filter(inst => inst.category === category)
      : instructions;
    
    // Filtrar por termo de busca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(inst => 
        inst.title.toLowerCase().includes(term) ||
        inst.content.toLowerCase().includes(term) ||
        inst.tags.some(tag => tag.toLowerCase().includes(term))
      );
    }
    
    return filtered.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } catch (error) {
    console.error('Erro ao buscar instruções nutricionais:', error);
    return [];
  }
}

/**
 * Busca uma instrução por ID
 */
export async function getNutritionInstructionById(
  instructionId: string,
  createdBy?: string
): Promise<NutritionInstruction | null> {
  try {
    // Buscar nas instruções do usuário
    if (createdBy) {
      const instructionRef = ref(database, `nutritionInstructions/${createdBy}/${instructionId}`);
      const snapshot = await rtdbGet(instructionRef);
      
      if (snapshot.exists()) {
        return {
          id: instructionId,
          ...snapshot.val(),
        };
      }
    }
    
    // Buscar em instruções públicas
    const publicRef = ref(database, 'nutritionInstructions');
    const publicSnapshot = await rtdbGet(publicRef);
    
    if (publicSnapshot.exists()) {
      const data = publicSnapshot.val();
      for (const [userId, userInstructions] of Object.entries(data)) {
        if (typeof userInstructions === 'object') {
          const instruction = (userInstructions as any)[instructionId];
          if (instruction && (instruction.isPublic || userId === createdBy)) {
            return {
              id: instructionId,
              ...instruction,
            };
          }
        }
      }
    }
    
    // Buscar na biblioteca do sistema
    const libraryRef = ref(database, 'nutritionInstructionsLibrary');
    const librarySnapshot = await rtdbGet(libraryRef);
    
    if (librarySnapshot.exists()) {
      const data = librarySnapshot.val();
      for (const [category, categoryInstructions] of Object.entries(data)) {
        if (typeof categoryInstructions === 'object') {
          const instruction = (categoryInstructions as any)[instructionId];
          if (instruction) {
            return {
              id: instructionId,
              ...instruction,
              createdBy: 'system',
              isPublic: true,
            };
          }
        }
      }
    }
    
    return null;
  } catch (error) {
    console.error('Erro ao buscar instrução:', error);
    return null;
  }
}

/**
 * Cria uma nova instrução nutricional
 */
export async function createNutritionInstruction(
  nutritionistId: string,
  instruction: Omit<NutritionInstruction, 'id' | 'createdBy' | 'createdAt'>
): Promise<string> {
  try {
    const instructionRef = push(ref(database, `nutritionInstructions/${nutritionistId}`));
    const instructionData: NutritionInstruction = {
      ...instruction,
      id: instructionRef.key as string,
      createdBy: nutritionistId,
      createdAt: new Date().toISOString(),
    };
    
    await rtdbSet(instructionRef, instructionData);
    return instructionRef.key as string;
  } catch (error) {
    console.error('Erro ao criar instrução nutricional:', error);
    throw new Error('Não foi possível criar a instrução');
  }
}

/**
 * Atualiza uma instrução nutricional
 */
export async function updateNutritionInstruction(
  nutritionistId: string,
  instructionId: string,
  updates: Partial<NutritionInstruction>
): Promise<void> {
  try {
    const instructionRef = ref(database, `nutritionInstructions/${nutritionistId}/${instructionId}`);
    await rtdbSet(instructionRef, {
      ...updates,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Erro ao atualizar instrução nutricional:', error);
    throw new Error('Não foi possível atualizar a instrução');
  }
}

/**
 * Deleta uma instrução nutricional
 */
export async function deleteNutritionInstruction(
  nutritionistId: string,
  instructionId: string
): Promise<void> {
  try {
    const instructionRef = ref(database, `nutritionInstructions/${nutritionistId}/${instructionId}`);
    await rtdbRemove(instructionRef);
  } catch (error) {
    console.error('Erro ao deletar instrução nutricional:', error);
    throw new Error('Não foi possível deletar a instrução');
  }
}

/**
 * Obtém todas as categorias disponíveis
 */
export function getInstructionCategories(): { value: InstructionCategory; label: string }[] {
  return [
    { value: 'general', label: 'Geral' },
    { value: 'weight_loss', label: 'Perda de Peso' },
    { value: 'weight_gain', label: 'Ganho de Peso' },
    { value: 'diabetes', label: 'Diabetes' },
    { value: 'hypertension', label: 'Hipertensão' },
    { value: 'cholesterol', label: 'Colesterol' },
    { value: 'pregnancy', label: 'Gravidez' },
    { value: 'sports', label: 'Esportes' },
    { value: 'vegetarian', label: 'Vegetariano' },
    { value: 'vegan', label: 'Vegano' },
    { value: 'digestive', label: 'Digestivo' },
    { value: 'allergies', label: 'Alergias' },
    // Categorias de Alimentação
    { value: 'nutrition', label: 'Nutrição' },
    { value: 'meal_planning', label: 'Planejamento de Refeições' },
    { value: 'recipes', label: 'Receitas' },
    { value: 'meal_timing', label: 'Horários das Refeições' },
    { value: 'hydration', label: 'Hidratação' },
    { value: 'supplements', label: 'Suplementos' },
    // Categorias de Exercícios
    { value: 'exercise', label: 'Exercícios' },
    { value: 'cardio', label: 'Cardio' },
    { value: 'strength_training', label: 'Treino de Força' },
    { value: 'flexibility', label: 'Flexibilidade' },
    { value: 'recovery', label: 'Recuperação' },
    { value: 'pre_workout_nutrition', label: 'Nutrição Pré-Treino' },
    { value: 'post_workout_nutrition', label: 'Nutrição Pós-Treino' },
  ];
}

